import { memo, useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Gavel, Users, Bot, Info, Clock, RefreshCw, ShieldCheck, Scale,
  CheckCircle, TrendingDown, TrendingUp,
} from 'lucide-react';
import {
  AppealStatus, JuryStageVotes, V2JuryStage, V2JuryStageVote,
  V2Stage1Result, V2Stage2Result, V2StageVoteChoice,
} from '../../types/appeals';
import { normalizeJuryStageVotes } from '../../utils/appealsV2';
import { appealsApiService } from '../../services/appealsApi';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';

interface V2JuryStageVotesSectionProps {
  appealId: number;
  appealStatus?: AppealStatus;
  juryStage?: V2JuryStage | null;
  stage1Result?: V2Stage1Result | null;
  stage2Result?: V2Stage2Result | null;
  initialVotes?: JuryStageVotes | null;
  className?: string;
}

interface VoteChoiceMeta {
  label: string;
  text: string;
  bg: string;
  border: string;
  bar: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const VOTE_META: Record<V2StageVoteChoice, VoteChoiceMeta> = {
  uphold: { label: 'Uphold', text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', bar: 'bg-red-500', Icon: ShieldCheck },
  vary: { label: 'Vary', text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', bar: 'bg-blue-500', Icon: Scale },
  remove: { label: 'Remove', text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', bar: 'bg-green-500', Icon: CheckCircle },
  reduce: { label: 'Reduce', text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/20', bar: 'bg-teal-500', Icon: TrendingDown },
  increase: { label: 'Increase', text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20', bar: 'bg-orange-500', Icon: TrendingUp },
};

const PENDING_META = { label: 'Pending', text: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/20' };

const StageVoteRow = memo(({ vote }: { vote: V2JuryStageVote }) => {
  const meta = vote.vote ? VOTE_META[vote.vote] : null;
  const Icon = meta?.Icon ?? Clock;
  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={cn('w-4 h-4 flex-shrink-0', meta?.text ?? 'text-gray-400')} />
        <div className="min-w-0">
          {vote.juror_minecraft_uuid ? (
            <PlayerProfile uuid={vote.juror_minecraft_uuid} inline showUuid={false} />
          ) : (
            <span className="text-sm text-glass-300 font-mono">{vote.juror_discord_id}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-glass-400 flex-shrink-0">
        <span className={cn('font-semibold', meta?.text ?? 'text-gray-400')}>
          {meta?.label ?? 'Pending'}
        </span>
        {vote.voted_at && (
          <span className="tabular-nums">{formatDistanceToNow(new Date(vote.voted_at), { addSuffix: true })}</span>
        )}
      </div>
    </div>
  );
});

const StagePanel = memo(({ title, question, votes, choices, resultChip, footnote }: {
  title: string;
  question: string;
  votes: V2JuryStageVote[];
  choices: V2StageVoteChoice[];
  resultChip?: { label: string; className: string } | null;
  footnote?: string;
}) => {
  const counts = choices.map(c => ({ choice: c, meta: VOTE_META[c], count: votes.filter(v => v.vote === c).length }));
  const pendingCount = votes.filter(v => v.vote === null).length;
  const votedCount = votes.length - pendingCount;

  return (
    <div className="subcard p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-white">{title}</span>
          <span className="text-xs text-glass-400 truncate">{question}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {resultChip && (
            <span className={cn('badge', resultChip.className)}>
              {resultChip.label}
            </span>
          )}
          <span className="text-xs text-glass-400 tabular-nums inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {votedCount}/{votes.length} voted
          </span>
        </div>
      </div>

      <div className={cn('grid gap-2', choices.length === 2 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4')}>
        {counts.map(({ choice, meta, count }) => (
          <div key={choice} className={cn('rounded-lg p-2 text-center border', meta.bg, meta.border)}>
            <p className={cn('text-xl font-semibold tabular-nums', meta.text)}>{count}</p>
            <p className="text-xs text-glass-400">{meta.label}</p>
          </div>
        ))}
        <div className={cn('rounded-lg p-2 text-center border', PENDING_META.bg, PENDING_META.border)}>
          <p className={cn('text-xl font-semibold tabular-nums', PENDING_META.text)}>{pendingCount}</p>
          <p className="text-xs text-glass-400">Pending</p>
        </div>
      </div>

      {votedCount > 0 && votes.length > 0 && (
        <div className="w-full bg-glass-100/10 rounded-full h-2 overflow-hidden flex">
          {counts.filter(c => c.count > 0).map(({ choice, meta, count }) => (
            <div key={choice} className={cn('h-full', meta.bar)} style={{ width: `${(count / votes.length) * 100}%` }} />
          ))}
        </div>
      )}

      <div className="divide-y divide-glass-200/10">
        {votes.map(vote => <StageVoteRow key={vote.id} vote={vote} />)}
        {votes.length === 0 && (
          <p className="text-xs text-glass-400 py-1">No votes recorded for this stage yet.</p>
        )}
      </div>

      {footnote && (
        <p className="text-xs text-glass-500">{footnote}</p>
      )}
    </div>
  );
});

/**
 * Jury V2 two-stage vote display. The V1 `JuryVotesSection` is untouched —
 * the detail view dispatches between the two based on `appeal_version`.
 *
 * Seeded from the `jury_stage_votes` object returned inline by
 * `GET /appeals/{id}`; the refresh button re-fetches just the jury via the
 * dedicated `GET /appeals/{id}/jury/v2` endpoint (live tail during voting,
 * without refetching the whole appeal).
 */
export const V2JuryStageVotesSection = memo(({
  appealId, appealStatus, juryStage, stage1Result, stage2Result, initialVotes, className,
}: V2JuryStageVotesSectionProps) => {
  const [votes, setVotes] = useState<JuryStageVotes>(() => normalizeJuryStageVotes(initialVotes));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    setVotes(normalizeJuryStageVotes(initialVotes));
  }, [initialVotes]);

  const refreshVotes = useCallback(async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      const data = await appealsApiService.getJuryStageVotes(appealId);
      setVotes(normalizeJuryStageVotes(data));
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Failed to refresh jury votes');
    } finally {
      setIsRefreshing(false);
    }
  }, [appealId]);

  const isInJury = appealStatus === 'in_jury';
  const isJuryDecided = appealStatus === 'jury_decided';
  const isStaffAnswered = appealStatus === 'staff_answered';

  const hasJurors = votes.stage1.length > 0 || votes.stage2.length > 0;
  const showStage2 = votes.stage2.length > 0 || juryStage === 2 || !!stage2Result;

  const stage1Chip = stage1Result === 'upheld'
    ? { label: 'Result: Upheld', className: 'bg-red-500/15 text-red-300 border-red-500/25' }
    : stage1Result === 'varied'
      ? { label: 'Result: Varied', className: 'bg-blue-500/15 text-blue-300 border-blue-500/25' }
      : null;

  const stage2Chip = stage2Result
    ? {
        label: `Result: ${stage2Result === 'made_unappealable' ? 'Made Unappealable' : VOTE_META[stage2Result as V2StageVoteChoice]?.label ?? stage2Result}`,
        className: stage2Result === 'remove' ? 'bg-green-500/15 text-green-300 border-green-500/25'
          : stage2Result === 'reduce' ? 'bg-teal-500/15 text-teal-300 border-teal-500/25'
          : 'bg-orange-500/15 text-orange-300 border-orange-500/25',
      }
    : null;

  return (
    <div className={cn('glass-card p-4 md:p-5 space-y-4', className)}>
      <SectionHeader
        className="mb-0"
        title={
          <span className="inline-flex items-center gap-2 flex-wrap">
            Jury Votes
            <span className="badge badge-purple">
              V2 · Two-Stage
            </span>
            {(isInJury || isJuryDecided) && (
              <span className="badge badge-purple">
                <Bot className="w-3 h-3" />
                Mr.Roboto
              </span>
            )}
          </span>
        }
        icon={Gavel}
        iconClassName="text-purple-400"
        actions={
          <button
            onClick={refreshVotes}
            className="glass-button-sm"
            disabled={isRefreshing}
            title="Refresh jury votes without reloading the whole appeal"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
            Refresh votes
          </button>
        }
      />

      {refreshError && (
        <div className="alert alert-danger text-xs py-2" role="alert">
          <span>{refreshError}</span>
        </div>
      )}

      {isStaffAnswered && !hasJurors && (
        <div className="alert bg-purple-500/10 border-purple-500/25" role="status">
          <Bot className="text-purple-400" />
          <div>
            <p className="text-sm text-purple-300 font-medium">Waiting for Mr.Roboto</p>
            <p className="text-xs text-glass-400 mt-0.5">
              Mr.Roboto will automatically create a jury case and select jurors.
              This typically happens within a few minutes.
            </p>
          </div>
        </div>
      )}

      {isInJury && (
        <div className="alert bg-purple-500/10 border-purple-500/25" role="status">
          <Info className="text-purple-400" />
          <p className="text-xs text-glass-400">
            {juryStage === 2
              ? 'Stage 2 voting is underway in Discord via Mr.Roboto: the jury voted to vary the ban and is now choosing how (remove, reduce, or increase).'
              : 'Stage 1 voting is underway in Discord via Mr.Roboto: the jury is deciding whether to uphold the ban as-is or vary it.'}
            {' '}Votes appear here as they are cast. Staff cannot influence the vote — only escalate or cancel if needed.
          </p>
        </div>
      )}

      {isJuryDecided && (
        <div className="alert alert-info" role="status">
          <Info />
          <p className="text-xs text-glass-400">
            The jury has finished voting. Mr.Roboto is finalizing the outcome and will update the appeal status shortly.
          </p>
        </div>
      )}

      {!hasJurors && !isStaffAnswered ? (
        <p className="text-sm text-glass-400">No jury has been selected yet.</p>
      ) : hasJurors && (
        <div className="space-y-3">
          <StagePanel
            title="Stage 1"
            question="Should the ban be kept as-is?"
            votes={votes.stage1}
            choices={['uphold', 'vary']}
            resultChip={stage1Chip}
          />
          {showStage2 && (
            <StagePanel
              title="Stage 2"
              question="How should the ban be varied?"
              votes={votes.stage2}
              choices={['remove', 'reduce', 'increase']}
              resultChip={stage2Chip}
              footnote="The whole jury votes in Stage 2 — including jurors who voted Uphold in Stage 1."
            />
          )}
        </div>
      )}
    </div>
  );
});
